import React, { useState, useEffect } from 'react';
import { Search, Calendar, Package, User, Truck, Eye, Printer, Send, DollarSign, CreditCard } from 'lucide-react';
import { useApp } from '../../context/AppContext';
import Button from '../../components/ui/Button';
import Input from '../../components/forms/Input';
import apiService from '../../services/apiService';
import SucursalFilter from '../../components/filters/SucursalFilter';
import { formatDate, formatMoney } from '../../utils/formatters';
import './MedidoHoyPage.css';

// Función auxiliar para obtener la fecha de hoy en formato YYYY-MM-DD
const getTodayDate = () => {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, '0');
  const day = String(today.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Función para obtener la fecha de ayer
const getYesterdayDate = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const year = yesterday.getFullYear();
  const month = String(yesterday.getMonth() + 1).padStart(2, '0');
  const day = String(yesterday.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Función para obtener el inicio de la semana (lunes)
const getStartOfWeek = () => {
  const today = new Date();
  const day = today.getDay(); // 0 = domingo, 1 = lunes, etc.
  const diff = day === 0 ? -6 : 1 - day; // Si es domingo, retroceder 6 días, sino calcular diferencia al lunes
  const monday = new Date(today);
  monday.setDate(today.getDate() + diff);

  const year = monday.getFullYear();
  const month = String(monday.getMonth() + 1).padStart(2, '0');
  const dayOfMonth = String(monday.getDate()).padStart(2, '0');
  return `${year}-${month}-${dayOfMonth}`;
};

// Función para obtener el fin de la semana (domingo)
const getEndOfWeek = () => {
  const today = new Date();
  const day = today.getDay();
  const diff = day === 0 ? 0 : 7 - day; // Si es domingo, 0, sino calcular días hasta domingo
  const sunday = new Date(today);
  sunday.setDate(today.getDate() + diff);

  const year = sunday.getFullYear();
  const month = String(sunday.getMonth() + 1).padStart(2, '0');
  const dayOfMonth = String(sunday.getDate()).padStart(2, '0');
  return `${year}-${month}-${dayOfMonth}`;
};

const MedidoHoyPage = () => {
  const { loading, setLoading, showToast } = useApp();

  const [searchQuery, setSearchQuery] = useState('');
  const [sucursalId, setSucursalId] = useState('');
  const [registros, setRegistros] = useState([]);
  const [filteredRegistros, setFilteredRegistros] = useState([]);
  const [selectedRegistros, setSelectedRegistros] = useState([]);
  const [expandedVentaId, setExpandedVentaId] = useState(null);
  const [ventaDetalle, setVentaDetalle] = useState(null);

  // Estados para filtros de fecha
  const [filtroFecha, setFiltroFecha] = useState('hoy'); // '', 'hoy', 'rango'
  const [fechaInicio, setFechaInicio] = useState('');
  const [fechaFin, setFechaFin] = useState('');

  // Estados para filtros adicionales
  const [filtroEstado, setFiltroEstado] = useState(''); // '', 'sin_estado', 'Almacen', etc.
  const [filtroEstatusPago, setFiltroEstatusPago] = useState(''); // '', 'pendiente', 'pagado'

  // Estados para edición de pago
  const [editandoPago, setEditandoPago] = useState(null); // ID de la venta siendo editada
  const [pagoTemporal, setPagoTemporal] = useState({ metodo_pago: '', estatus_pago: '' });

  useEffect(() => {
    loadData();
  }, [sucursalId]);

  useEffect(() => {
    filterRegistros();
  }, [registros, searchQuery, filtroFecha, fechaInicio, fechaFin, filtroEstado, filtroEstatusPago]);

  const loadData = async () => {
    try {
      setLoading(true);
      const result = await apiService.getVentas({ sucursal: sucursalId });

      if (result.success) {
        setRegistros(result.data || []);
      } else {
        showToast(result.message || 'Error al cargar pedidos', 'error');
      }
    } catch (error) {
      console.error('Error al cargar pedidos:', error);
      showToast('Error al cargar pedidos', 'error');
    } finally {
      setLoading(false);
    }
  };

  const filterRegistros = () => {
    let filtered = registros;

    // Filtrar por búsqueda de texto
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(registro => {
        return (
          registro.id?.toString().includes(query) ||
          registro.folio?.toLowerCase().includes(query) ||
          registro.cliente_nombre?.toLowerCase().includes(query)
        );
      });
    }

    // Filtrar por fecha
    if (filtroFecha === 'hoy') {
      const hoy = getTodayDate();
      filtered = filtered.filter(registro => {
        const fechaRegistro = registro.fecha_creacion ? registro.fecha_creacion.split(' ')[0] : '';
        return fechaRegistro === hoy;
      });
    } else if (filtroFecha === 'ayer') {
      const ayer = getYesterdayDate();
      filtered = filtered.filter(registro => {
        const fechaRegistro = registro.fecha_creacion ? registro.fecha_creacion.split(' ')[0] : '';
        return fechaRegistro === ayer;
      });
    } else if (filtroFecha === 'semana') {
      const inicioSemana = getStartOfWeek();
      const finSemana = getEndOfWeek();
      filtered = filtered.filter(registro => {
        const fechaRegistro = registro.fecha_creacion ? registro.fecha_creacion.split(' ')[0] : '';
        return fechaRegistro >= inicioSemana && fechaRegistro <= finSemana;
      });
    } else if (filtroFecha === 'rango' && fechaInicio && fechaFin) {
      filtered = filtered.filter(registro => {
        const fechaRegistro = registro.fecha_creacion ? registro.fecha_creacion.split(' ')[0] : '';
        return fechaRegistro >= fechaInicio && fechaRegistro <= fechaFin;
      });
    }

    // Filtrar por estado
    if (filtroEstado === 'sin_estado') {
      filtered = filtered.filter(registro => !registro.estado || registro.estado === '');
    } else if (filtroEstado) {
      filtered = filtered.filter(registro => registro.estado === filtroEstado);
    }

    // Filtrar por estatus de pago
    if (filtroEstatusPago) {
      filtered = filtered.filter(registro => {
        if (filtroEstatusPago === 'pendiente') {
          return !registro.estatus_pago || registro.estatus_pago === 'pendiente';
        }
        return registro.estatus_pago === filtroEstatusPago;
      });
    }

    setFilteredRegistros(filtered);
  };

  const handleSelectRegistro = (registroId) => {
    setSelectedRegistros(prev => {
      if (prev.includes(registroId)) {
        return prev.filter(id => id !== registroId);
      } else {
        return [...prev, registroId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedRegistros.length === filteredRegistros.length) {
      setSelectedRegistros([]);
    } else {
      setSelectedRegistros(filteredRegistros.map(registro => registro.id));
    }
  };

  const isRegistroSelected = (registroId) => {
    return selectedRegistros.includes(registroId);
  };

  const areAllSelected = () => {
    return filteredRegistros.length > 0 && selectedRegistros.length === filteredRegistros.length;
  };

  const areSomeSelected = () => {
    return selectedRegistros.length > 0 && selectedRegistros.length < filteredRegistros.length;
  };

  const handleViewDetail = async (venta) => {
    // Si ya está expandida, contraerla
    if (expandedVentaId === venta.id) {
      setExpandedVentaId(null);
      setVentaDetalle(null);
      return;
    }

    try {
      setLoading(true);

      // Obtener detalle completo de la venta
      const result = await apiService.getVenta(venta.id);

      if (result.success) {
        setVentaDetalle(result.data);
        setExpandedVentaId(venta.id);
      } else {
        showToast('Error al cargar detalle de pedido', 'error');
      }
    } catch (error) {
      console.error('Error al cargar detalle:', error);
      showToast('Error al cargar detalle de pedido', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handlePrint = async (venta) => {
    try {
      setLoading(true);

      // Obtener el ticket desde el API
      const ticketResult = await apiService.imprimirTicket(venta.id);

      if (ticketResult.success) {
        await imprimirTicket(ticketResult.data.ticket, ticketResult.data.folio);
        showToast('Ticket reenviado a impresora', 'success');
      } else {
        showToast('Error al generar ticket', 'error');
      }
    } catch (error) {
      console.error('Error al reimprimir ticket:', error);
      showToast('Error al reimprimir ticket', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para imprimir ticket usando Electron IPC
  const imprimirTicket = async (ticketTexto, folio) => {
    try {
      // Verificar si estamos en Electron
      if (!window.electron || !window.electron.ipcRenderer) {
        console.warn('Electron IPC no disponible, usando impresión del navegador');
        imprimirTicketNavegador(ticketTexto, folio);
        return;
      }

      // Obtener impresora configurada o usar la predeterminada del sistema
      let printerName = localStorage.getItem('ticket_printer');

      if (!printerName) {
        try {
          const result = await window.electron.ipcRenderer.invoke('get-default-printer');
          if (result.success && result.printer) {
            printerName = result.printer.name;
            console.log('Usando impresora predeterminada del sistema:', printerName);
          } else {
            printerName = 'Microsoft Print to PDF';
            console.warn('No se pudo obtener impresora predeterminada, usando fallback');
          }
        } catch (error) {
          console.error('Error al obtener impresora predeterminada:', error);
          printerName = 'Microsoft Print to PDF';
        }
      }

      // Convertir texto a HTML
      const htmlContent = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="utf-8">
          <title>Ticket - ${folio}</title>
          <style>
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              font-weight: bold;
              color: #000;
              margin: 0;
              padding: 5px;
              white-space: pre-wrap;
              background: white;
              line-height: 1.2;
              word-wrap: break-word;
              overflow-wrap: break-word;
              max-width: 100%;
              box-sizing: border-box;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            @media print {
              @page { margin: 0; size: 80mm auto; }
              body {
                margin: 0;
                padding: 2px;
                font-weight: bold;
                color: #000;
                max-width: 80mm;
              }
            }
          </style>
        </head>
        <body>${ticketTexto}</body>
        </html>
      `;

      console.log('Imprimiendo ticket en:', printerName);

      // Enviar a impresora usando Electron IPC
      const result = await window.electron.ipcRenderer.invoke('print-document', {
        printerName: printerName,
        htmlContent: htmlContent,
        silent: true,
        options: {
          margins: { marginType: 'none' },
          pageSize: 'A4'
        }
      });

      if (result.success) {
        console.log('Ticket impreso correctamente');
      } else {
        console.error('Error al imprimir ticket:', result.message);
        showToast('Error al imprimir ticket: ' + result.message, 'warning');
      }

    } catch (error) {
      console.error('Error en impresión de ticket:', error);
      showToast('Error al imprimir ticket', 'error');
    }
  };

  // Función de respaldo para imprimir en el navegador
  const imprimirTicketNavegador = (ticketTexto, folio) => {
    const ventanaImpresion = window.open('', 'Imprimir Ticket', 'width=400,height=600');

    if (ventanaImpresion) {
      ventanaImpresion.document.write(`
        <!DOCTYPE html>
        <html>
        <head>
          <title>Ticket - ${folio}</title>
          <style>
            body {
              font-family: 'Courier New', monospace;
              font-size: 12px;
              font-weight: bold;
              color: #000;
              margin: 0;
              padding: 10px;
              white-space: pre-wrap;
              background: white;
              word-wrap: break-word;
              overflow-wrap: break-word;
              max-width: 100%;
              box-sizing: border-box;
              -webkit-print-color-adjust: exact;
              print-color-adjust: exact;
            }
            @media print {
              body {
                margin: 0;
                padding: 5px;
                font-weight: bold;
                color: #000;
                max-width: 80mm;
              }
            }
          </style>
        </head>
        <body>${ticketTexto}</body>
        </html>
      `);

      ventanaImpresion.document.close();

      ventanaImpresion.onload = function () {
        ventanaImpresion.focus();
        setTimeout(() => {
          ventanaImpresion.print();
        }, 250);
      };
    }
  };

  // Función para mandar un pedido individual a picking
  const handleMandarAPicking = async (venta) => {
    try {
      setLoading(true);

      const result = await apiService.actualizarEstadoVenta(venta.id, 'Almacen');

      if (result.success) {
        showToast('Pedido enviado a Almacen', 'success');
        // Recargar los datos para reflejar el cambio
        loadData();
      } else {
        showToast(result.message || 'Error al enviar a Almacen', 'error');
      }
    } catch (error) {
      console.error('Error al enviar a Almacen:', error);
      showToast('Error al enviar a Almacen', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para mandar múltiples pedidos a picking
  const handleMandarMultiplesAPicking = async () => {
    if (selectedRegistros.length === 0) {
      showToast('No hay pedidos seleccionados', 'warning');
      return;
    }

    // Filtrar solo los pedidos sin estado (pueden ir a picking)
    const pedidosSinEstado = selectedRegistros.filter(id => {
      const pedido = registros.find(r => r.id === id);
      return pedido && (!pedido.estado || pedido.estado === '');
    });

    if (pedidosSinEstado.length === 0) {
      showToast('Los pedidos seleccionados ya tienen un estado asignado', 'warning');
      return;
    }

    if (pedidosSinEstado.length < selectedRegistros.length) {
      showToast(`Solo se procesarán ${pedidosSinEstado.length} de ${selectedRegistros.length} pedidos (los que no tienen estado)`, 'info');
    }

    try {
      setLoading(true);
      let exitosos = 0;
      let fallidos = 0;

      // Procesar cada pedido sin estado
      for (const ventaId of pedidosSinEstado) {
        try {
          const result = await apiService.actualizarEstadoVenta(ventaId, 'Almacen');
          if (result.success) {
            exitosos++;
          } else {
            fallidos++;
          }
        } catch (error) {
          console.error(`Error al procesar pedido ${ventaId}:`, error);
          fallidos++;
        }
      }

      // Mostrar resultado
      if (exitosos > 0 && fallidos === 0) {
        showToast(`${exitosos} pedido(s) enviado(s) a Almacen`, 'success');
      } else if (exitosos > 0 && fallidos > 0) {
        showToast(`${exitosos} enviado(s), ${fallidos} fallido(s)`, 'warning');
      } else {
        showToast('No se pudo enviar ningún pedido', 'error');
      }

      // Limpiar selección y recargar datos
      setSelectedRegistros([]);
      loadData();

    } catch (error) {
      console.error('Error al enviar pedidos a Almacen:', error);
      showToast('Error al procesar pedidos', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para iniciar edición de pago
  const handleIniciarEdicionPago = (registro) => {
    setEditandoPago(registro.id);
    setPagoTemporal({
      metodo_pago: registro.metodo_pago || '',  // Permitir vacío/null
      estatus_pago: registro.estatus_pago || 'pendiente'
    });
  };

  // Función para cancelar edición
  const handleCancelarEdicionPago = () => {
    setEditandoPago(null);
    setPagoTemporal({ metodo_pago: '', estatus_pago: '' });
  };

  // Función para guardar cambios de pago
  const handleGuardarPago = async (ventaId, registro) => {
    // Validar si se está intentando cambiar a crédito
    if (pagoTemporal.metodo_pago === 'credito') {
      if (!registro.cliente_id) {
        showToast('Se requiere un cliente para ventas a crédito', 'warning');
        return;
      }
      if (!registro.cliente_tiene_credito) {
        showToast('El cliente no tiene crédito activado', 'warning');
        return;
      }
      const creditoDisponible = (registro.cliente_monto_credito_maximo || 0) - (registro.cliente_saldo_credito_usado || 0);
      if (creditoDisponible <= 0) {
        showToast('El cliente no tiene crédito disponible', 'warning');
        return;
      }
      if (registro.total > creditoDisponible) {
        showToast(`El total ($${registro.total.toFixed(2)}) excede el crédito disponible ($${creditoDisponible.toFixed(2)})`, 'warning');
        return;
      }
    }

    try {
      setLoading(true);

      // Convertir string vacío a null
      const metodoPagoFinal = pagoTemporal.metodo_pago === '' ? null : pagoTemporal.metodo_pago;

      const result = await apiService.actualizarPagoVenta(
        ventaId,
        metodoPagoFinal,
        pagoTemporal.estatus_pago
      );

      if (result.success) {
        showToast('Pago actualizado correctamente', 'success');
        setEditandoPago(null);
        setPagoTemporal({ metodo_pago: '', estatus_pago: '' });
        // Recargar los datos para reflejar el cambio
        loadData();
      } else {
        showToast(result.message || 'Error al actualizar pago', 'error');
      }
    } catch (error) {
      console.error('Error al actualizar pago:', error);
      showToast('Error al actualizar pago', 'error');
    } finally {
      setLoading(false);
    }
  };

  // Función para toggle rápido del estatus de pago
  const handleToggleEstatusPago = async (registro) => {
    const nuevoEstatus = registro.estatus_pago === 'pagado' ? 'pendiente' : 'pagado';

    try {
      setLoading(true);

      const result = await apiService.actualizarPagoVenta(
        registro.id,
        registro.metodo_pago || null,  // Mantener null si no tiene método
        nuevoEstatus
      );

      if (result.success) {
        showToast(`Marcado como ${nuevoEstatus}`, 'success');
        loadData();
      } else {
        showToast(result.message || 'Error al actualizar pago', 'error');
      }
    } catch (error) {
      console.error('Error al actualizar estatus:', error);
      showToast('Error al actualizar estatus', 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="medido-hoy-page">
      {/* Controles */}
      <div className="medido-hoy-controls">
        <div className="search-filters">
          {/* Búsqueda */}
          <div className="search-container">
            <Input
              placeholder="Buscar por folio, cliente..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              icon={<Search size={20} />}
              className="search-input"
            />
          </div>

          {/* Filtros por fecha */}
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filtroFecha === 'ayer' ? 'active' : ''}`}
              onClick={() => setFiltroFecha(filtroFecha === 'ayer' ? '' : 'ayer')}
            >
              <Calendar size={14} />
              Ayer
            </button>
            <button
              className={`filter-btn ${filtroFecha === 'semana' ? 'active' : ''}`}
              onClick={() => setFiltroFecha(filtroFecha === 'semana' ? '' : 'semana')}
            >
              <Calendar size={14} />
              Semana
            </button>
            <button
              className={`filter-btn ${filtroFecha === 'hoy' ? 'active' : ''}`}
              onClick={() => setFiltroFecha(filtroFecha === 'hoy' ? '' : 'hoy')}
            >
              <Calendar size={14} />
              Hoy
            </button>
            <button
              className={`filter-btn ${filtroFecha === 'rango' ? 'active' : ''}`}
              onClick={() => {
                if (filtroFecha === 'rango') {
                  setFiltroFecha('');
                } else {
                  setFiltroFecha('rango');
                  setFechaInicio(getStartOfWeek());
                  setFechaFin(getEndOfWeek());
                }
              }}
            >
              <Calendar size={14} />
              Rango
            </button>
            {filtroFecha === 'rango' && (
              <>
                <input
                  type="date"
                  value={fechaInicio}
                  onChange={(e) => setFechaInicio(e.target.value)}
                  className="date-input"
                />
                <span style={{ color: '#6b7280', fontSize: '0.8125rem' }}>hasta</span>
                <input
                  type="date"
                  value={fechaFin}
                  onChange={(e) => setFechaFin(e.target.value)}
                  className="date-input"
                />
              </>
            )}
          </div>

          {/* Filtro por estado */}
          <div className="filter-buttons">
            <select
              className={`filter-select ${filtroEstado ? 'active' : ''}`}
              value={filtroEstado}
              onChange={(e) => setFiltroEstado(e.target.value)}
            >
              <option value="">Estado: Todos</option>
              <option value="sin_estado">Sin estado</option>
              {[...new Set(registros.filter(r => r.estado).map(r => r.estado))].map(estado => (
                <option key={estado} value={estado}>{estado}</option>
              ))}
            </select>
          </div>

          {/* Filtro por estatus de pago */}
          <div className="filter-buttons">
            <button
              className={`filter-btn ${filtroEstatusPago === 'pendiente' ? 'active' : ''}`}
              onClick={() => setFiltroEstatusPago(filtroEstatusPago === 'pendiente' ? '' : 'pendiente')}
            >
              <DollarSign size={14} />
              Pendiente
            </button>
            <button
              className={`filter-btn ${filtroEstatusPago === 'pagado' ? 'active' : ''}`}
              onClick={() => setFiltroEstatusPago(filtroEstatusPago === 'pagado' ? '' : 'pagado')}
            >
              <DollarSign size={14} />
              Pagado
            </button>
          </div>

          <SucursalFilter value={sucursalId} onChange={(val) => { setSucursalId(val); }} />
        </div>

        <div className="controls-actions">
          {selectedRegistros.length > 0 && (
            <Button
              onClick={handleMandarMultiplesAPicking}
              disabled={loading}
            >
              Mandar a Almacen ({selectedRegistros.length})
            </Button>
          )}
        </div>
      </div>

      {/* Tabla de registros */}
      <div className="medido-hoy-table-container">
        {loading && (
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Cargando registros...</p>
          </div>
        )}

        {!loading && filteredRegistros.length === 0 && (
          <div className="empty-state">
            <Package size={48} />
            <h3>No se encontraron registros</h3>
            <p>
              {searchQuery
                ? 'No hay registros que coincidan con la búsqueda'
                : 'No hay registros disponibles'
              }
            </p>
          </div>
        )}

        {!loading && filteredRegistros.length > 0 && (
          <table className="medido-hoy-table">
            <thead>
              <tr>
                <th className="checkbox-column">
                  <input
                    type="checkbox"
                    checked={areAllSelected()}
                    ref={input => {
                      if (input) {
                        input.indeterminate = areSomeSelected();
                      }
                    }}
                    onChange={handleSelectAll}
                    title={areAllSelected() ? 'Deseleccionar todas' : 'Seleccionar todas'}
                  />
                </th>
                <th>Folio</th>
                <th>Fecha</th>
                <th>Cliente</th>
                <th>Entrega</th>
                <th>Productos</th>
                <th>Método Pago</th>
                <th>Estatus Pago</th>
                <th>Estado</th>
                <th data-type="actions">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredRegistros.map((registro) => (
                <React.Fragment key={registro.id}>
                  <tr className={`${isRegistroSelected(registro.id) ? 'selected-row' : ''} ${expandedVentaId === registro.id ? 'expanded-row' : ''}`}>
                    <td className="checkbox-column">
                      <input
                        type="checkbox"
                        checked={isRegistroSelected(registro.id)}
                        onChange={() => handleSelectRegistro(registro.id)}
                        onClick={(e) => e.stopPropagation()}
                      />
                    </td>
                    <td>
                      <div className="registro-folio">
                        <span className="folio-number">#{registro.folio || registro.id}</span>
                      </div>
                    </td>

                    <td>
                      <div className="registro-fecha">
                        <Calendar size={14} />
                        <span>{formatDate(registro.fecha_creacion)}</span>
                      </div>
                    </td>

                    <td>
                      <div className="registro-cliente">
                        <User size={14} />
                        <div className="cliente-info">
                          <span className="cliente-nombre">{registro.cliente_nombre || 'Cliente General'}</span>
                        </div>
                      </div>
                    </td>

                    <td>
                      <div className="registro-entrega">
                        {registro.tipo_entrega === 'domicilio' ? (
                          <>
                            <Truck size={14} />
                            <span>Domicilio</span>
                          </>
                        ) : (
                          <>
                            <Package size={14} />
                            <span>Pickup</span>
                          </>
                        )}
                      </div>
                    </td>

                    <td>
                      <div className="registro-productos">
                        <Package size={14} />
                        <span>{registro.total_productos || 0} productos</span>
                      </div>
                    </td>

                    <td colSpan={editandoPago === registro.id ? 2 : 1}>
                      {editandoPago === registro.id ? (
                        <div style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: '8px',
                          padding: '8px',
                          background: '#f9fafb',
                          borderRadius: '6px'
                        }}>
                          {/* Lista desplegable de método de pago */}
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            <label style={{ fontSize: '11px', fontWeight: 600, color: '#6b7280' }}>Método de Pago:</label>
                            <select
                              value={pagoTemporal.metodo_pago}
                              onChange={(e) => {
                                const nuevoMetodo = e.target.value === '' ? null : e.target.value;

                                // Validar si se selecciona crédito
                                if (nuevoMetodo === 'credito') {
                                  if (!registro.cliente_id) {
                                    showToast('Se requiere un cliente para ventas a crédito', 'warning');
                                    return;
                                  }
                                  if (!registro.cliente_tiene_credito) {
                                    showToast('El cliente no tiene crédito activado', 'warning');
                                    return;
                                  }
                                  const creditoDisponible = (registro.cliente_monto_credito_maximo || 0) - (registro.cliente_saldo_credito_usado || 0);
                                  if (creditoDisponible <= 0) {
                                    showToast('El cliente no tiene crédito disponible', 'warning');
                                    return;
                                  }
                                  if (registro.total > creditoDisponible) {
                                    showToast(`El total ($${registro.total.toFixed(2)}) excede el crédito disponible ($${creditoDisponible.toFixed(2)})`, 'warning');
                                    return;
                                  }
                                }

                                setPagoTemporal({ ...pagoTemporal, metodo_pago: nuevoMetodo });
                              }}
                              disabled={loading}
                              style={{
                                padding: '8px 10px',
                                border: '1px solid #e5e7eb',
                                borderRadius: '4px',
                                fontSize: '13px',
                                fontWeight: 500,
                                color: '#374151',
                                background: 'white',
                                cursor: loading ? 'not-allowed' : 'pointer'
                              }}
                            >
                              <option value="">Sin asignar</option>
                              <option value="efectivo">💵 Efectivo</option>
                              <option value="tarjeta">💳 Tarjeta</option>
                              <option value="transferencia">🏦 Transferencia</option>
                              <option
                                value="credito"
                                disabled={!registro.cliente_id || !registro.cliente_tiene_credito || ((registro.cliente_monto_credito_maximo || 0) - (registro.cliente_saldo_credito_usado || 0)) <= 0}
                              >
                                📝 Crédito {(!registro.cliente_id || !registro.cliente_tiene_credito || ((registro.cliente_monto_credito_maximo || 0) - (registro.cliente_saldo_credito_usado || 0)) <= 0) ? '(No disponible)' : ''}
                              </option>
                            </select>
                          </div>

                          {/* Botones de estatus de pago */}
                          <div style={{ display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap' }}>
                            <button
                              type="button"
                              onClick={() => setPagoTemporal({ ...pagoTemporal, estatus_pago: 'pendiente' })}
                              disabled={loading}
                              style={{
                                padding: '6px 10px',
                                border: pagoTemporal.estatus_pago === 'pendiente' ? '2px solid #ef4444' : '1px solid #e5e7eb',
                                borderRadius: '4px',
                                background: pagoTemporal.estatus_pago === 'pendiente' ? '#fee2e2' : 'white',
                                color: pagoTemporal.estatus_pago === 'pendiente' ? '#991b1b' : '#6b7280',
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: loading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <span>○</span>
                              <span>Pendiente</span>
                            </button>

                            <button
                              type="button"
                              onClick={() => setPagoTemporal({ ...pagoTemporal, estatus_pago: 'pagado' })}
                              disabled={loading}
                              style={{
                                padding: '6px 10px',
                                border: pagoTemporal.estatus_pago === 'pagado' ? '2px solid #10b981' : '1px solid #e5e7eb',
                                borderRadius: '4px',
                                background: pagoTemporal.estatus_pago === 'pagado' ? '#d1fae5' : 'white',
                                color: pagoTemporal.estatus_pago === 'pagado' ? '#065f46' : '#6b7280',
                                fontSize: '12px',
                                fontWeight: 600,
                                cursor: loading ? 'not-allowed' : 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}
                            >
                              <span>✓</span>
                              <span>Pagado</span>
                            </button>

                            {/* Botones de acción */}
                            <div style={{ display: 'flex', gap: '4px', marginLeft: 'auto' }}>
                              <button
                                onClick={() => handleGuardarPago(registro.id, registro)}
                                disabled={loading}
                                title="Guardar cambios"
                                style={{
                                  padding: '6px 12px',
                                  border: 'none',
                                  borderRadius: '4px',
                                  background: '#10b981',
                                  color: 'white',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  cursor: loading ? 'not-allowed' : 'pointer'
                                }}
                              >
                                ✓ Guardar
                              </button>
                              <button
                                onClick={handleCancelarEdicionPago}
                                disabled={loading}
                                title="Cancelar"
                                style={{
                                  padding: '6px 12px',
                                  border: '1px solid #e5e7eb',
                                  borderRadius: '4px',
                                  background: 'white',
                                  color: '#6b7280',
                                  fontSize: '12px',
                                  fontWeight: 600,
                                  cursor: loading ? 'not-allowed' : 'pointer'
                                }}
                              >
                                ✕ Cancelar
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="metodo-pago" onClick={() => handleIniciarEdicionPago(registro)}>
                          <CreditCard size={14} />
                          <span>{registro.metodo_pago || 'Sin asignar'}</span>
                        </div>
                      )}
                    </td>

                    {editandoPago !== registro.id && (
                      <td>
                        <div className="status-pago-container">
                          <span className={`status-pago ${registro.estatus_pago === 'pagado' ? 'pagado' : 'pendiente'}`}>
                            {registro.estatus_pago === 'pagado' ? '✓ Pagado' : '○ Pendiente'}
                          </span>
                          <button
                            className="toggle-pago-btn"
                            onClick={() => handleToggleEstatusPago(registro)}
                            disabled={loading}
                            title={registro.estatus_pago === 'pagado' ? 'Marcar como pendiente' : 'Marcar como pagado'}
                          >
                            <DollarSign size={12} />
                          </button>
                        </div>
                      </td>
                    )}

                    <td>
                      {registro.estado && (
                        <span className={`status-badge status-${registro.estado.toLowerCase()}`}>
                          {registro.estado}
                        </span>
                      )}
                    </td>

                    <td className="actions">
                      <div className="actions-compact">
                        <button
                          className={`action-btn-compact ${expandedVentaId === registro.id ? 'active' : ''}`}
                          onClick={() => handleViewDetail(registro)}
                          title={expandedVentaId === registro.id ? "Ocultar detalle" : "Ver detalle"}
                        >
                          <Eye size={14} />
                        </button>
                        {(!registro.estado || registro.estado === '' || registro.estado === null) && (
                          <button
                            className="action-btn-compact action-btn-picking"
                            onClick={() => handleMandarAPicking(registro)}
                            title="Mandar a Almacen"
                            disabled={loading}
                          >
                            <Send size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>

                  {/* Fila expandible con detalle */}
                  {expandedVentaId === registro.id && ventaDetalle && (
                    <tr className="detail-row">
                      <td colSpan="10">
                        <div className="detail-content">
                          {/* Información del pedido */}
                          <div className="detail-info-grid">
                            <div className="detail-info-item">
                              <label>Folio:</label>
                              <span>{ventaDetalle.folio || `#${ventaDetalle.id}`}</span>
                            </div>
                            <div className="detail-info-item">
                              <label>Atendió:</label>
                              <span>{ventaDetalle.usuario_nombre || 'N/A'}</span>
                            </div>
                            {ventaDetalle.tipo_entrega === 'domicilio' && ventaDetalle.direccion_entrega && (
                              <div className="detail-info-item full-width">
                                <label>Dirección:</label>
                                <span>{ventaDetalle.direccion_entrega}</span>
                              </div>
                            )}
                          </div>

                          {/* Productos */}
                          <div className="detail-products">
                            <h4>Productos</h4>
                            <table className="products-table">
                              <thead>
                                <tr>
                                  <th>Cantidad</th>
                                  <th>Producto</th>
                                  <th>Precio Unit.</th>
                                  <th>Subtotal</th>
                                </tr>
                              </thead>
                              <tbody>
                                {ventaDetalle.detalles && ventaDetalle.detalles.length > 0 ? (
                                  ventaDetalle.detalles.map((detalle, index) => (
                                    <tr key={index}>
                                      <td>{detalle.cantidad}</td>
                                      <td>{detalle.producto_nombre}</td>
                                      <td>{formatMoney(detalle.precio_unitario)}</td>
                                      <td>{formatMoney(detalle.subtotal)}</td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan="4" style={{ textAlign: 'center' }}>No hay productos</td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>

                          {/* Totales y acciones */}
                          <div className="detail-footer">
                            <div className="detail-totals-compact">
                              <div className="total-line">
                                <span>Subtotal:</span>
                                <span>{formatMoney(ventaDetalle.subtotal || 0)}</span>
                              </div>
                              {ventaDetalle.descuento > 0 && (
                                <div className="total-line">
                                  <span>Descuento:</span>
                                  <span className="text-danger">-{formatMoney(ventaDetalle.descuento)}</span>
                                </div>
                              )}
                              <div className="total-line total-final">
                                <span>Total:</span>
                                <span>{formatMoney(ventaDetalle.total || 0)}</span>
                              </div>
                            </div>
                            <Button
                              onClick={() => handlePrint(ventaDetalle)}
                              size="small"
                            >
                              <Printer size={16} />
                              Reimprimir Ticket
                            </Button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
};

export default MedidoHoyPage;
